/**
 * 错误处理工具函数
 * 统一处理API响应错误，确保后台返回的错误信息能正确显示
 */

import { message } from 'antd';

/**
 * 处理API响应错误
 * @param error 错误对象
 * @param defaultMessage 默认错误信息
 * @param showMessage 是否显示错误消息，默认为true
 * @returns 错误信息字符串
 */
export const handleApiError = (
  error: any,
  defaultMessage: string = '操作失败，请重试',
  showMessage: boolean = true
): string => {
  let errorMessage = defaultMessage;

  // 优先获取后台返回的错误信息
  if (error?.response?.data?.message) {
    errorMessage = error.response.data.message;
  } else if (error?.message) {
    errorMessage = error.message;
  } else if (typeof error === 'string') {
    errorMessage = error;
  }

  // 控制台输出详细错误信息用于调试
  console.error('[API Error]:', {
    error,
    errorMessage,
    response: error?.response,
    data: error?.response?.data
  });

  // 显示错误消息
  if (showMessage) {
    message.error(errorMessage);
  }

  return errorMessage;
};

/**
 * 处理API响应结果
 * @param response API响应对象
 * @param successMessage 成功消息
 * @param showSuccessMessage 是否显示成功消息，默认为true
 * @returns 是否成功
 */
export const handleApiResponse = (
  response: any,
  successMessage: string = '操作成功',
  showSuccessMessage: boolean = true
): boolean => {
  if (response.code === 200) {
    if (showSuccessMessage) {
      message.success(successMessage);
    }
    return true;
  } else {
    // 显示后台返回的错误信息
    const errorMessage = response.message || '操作失败';
    message.error(errorMessage);
    return false;
  }
};

/**
 * 创建带错误处理的异步函数包装器
 * @param asyncFn 异步函数
 * @param defaultErrorMessage 默认错误信息
 * @param showErrorMessage 是否显示错误消息
 * @returns 包装后的函数
 */
export const withErrorHandling = <T extends any[], R>(
  asyncFn: (...args: T) => Promise<R>,
  defaultErrorMessage: string = '操作失败，请重试',
  showErrorMessage: boolean = true
) => {
  return async (...args: T): Promise<R | null> => {
    try {
      return await asyncFn(...args);
    } catch (error) {
      handleApiError(error, defaultErrorMessage, showErrorMessage);
      return null;
    }
  };
};

/**
 * 处理表单提交错误
 * @param error 错误对象
 * @param form 表单实例
 * @param fieldErrors 字段错误映射
 */
export const handleFormError = (
  error: any,
  form?: any,
  fieldErrors?: Record<string, string>
) => {
  const errorMessage = handleApiError(error, '提交失败，请重试', false);

  // 如果有字段错误映射，设置表单字段错误
  if (form && fieldErrors) {
    const fields = Object.keys(fieldErrors);
    const errors = fields.map(field => ({
      name: field,
      errors: [fieldErrors[field]]
    }));
    form.setFields(errors);
  }

  // 显示错误消息
  message.error(errorMessage);
};

export default {
  handleApiError,
  handleApiResponse,
  withErrorHandling,
  handleFormError
};



